/**
 * 원격 설정 조각 — 정비 화면의 설정 중 콘솔에서도 정하는 것(화면 설정 창 `DisplaySettings` 가 쓴다).
 *
 *   자동 업데이트 · 부팅 시 자동 실행   정해 둔다. 꺼진 화면도 다시 붙을 때 받아 간다.
 *   기기 재부팅                         명령. 다시 켜지면 자동 실행으로 돌아온다.
 *   앱 종료 · 전원 종료                 명령. **원격으로 되돌릴 수 없다** — 현장에서 다시 켜야 한다.
 *   정비 모드 해제                      명령(0.7.17). 정비 중인 화면에만 — 현장에서 열어 둔 채 떠난 정비 화면을 닫는다.
 *
 * 명령은 켜진 화면에만, 그 명령을 아는(할 수 있는) 기기에만 건다. 서버 주소(잘못 바꾸면 화면이
 * 이 콘솔을 떠난다)와 네트워크(에이전트가 OS 망 설정을 바꾸지 못한다)는 현장 정비 화면에만 있다.
 *
 * 정한 값과 **기기의 지금 값**을 나란히 보인다. 콘솔은 정해 둘 뿐이고 실제로 그렇게 됐는지는
 * 화면만 안다 — 자동 실행은 셸의 설정이고, 재부팅·전원 종료는 기기 권한(루트·기기 소유자)에 달렸다.
 */
import type { ReactNode } from 'react';
import { LogOut, Play, Power, RotateCw } from 'lucide-react';
import type { AgentCommandKind, AgentControl, BulkCommandResult, Display } from '../api/client.js';
import { relativeTime } from './ui.js';
import { confirmDialog } from './ConfirmDialog.js';
import { MoreMenu } from './MoreMenu.js';
import { displayLive } from './displayLive.js';

type Choice = 'device' | 'on' | 'off';

export function choiceOf(value: boolean | null | undefined): Choice {
  return value === true ? 'on' : value === false ? 'off' : 'device';
}

export function valueOf(choice: string): boolean | null {
  return choice === 'on' ? true : choice === 'off' ? false : null;
}

/** 기기가 알려 온 지금 값. */
export function reportedText(control: AgentControl | undefined, key: 'auto_update' | 'autostart'): string {
  const reported = control?.reported;
  if (!reported) return '모름(에이전트 0.7.6 미만)';
  const value = reported[key];
  if (value === null) return '읽을 수 없음';
  return value ? '켜짐' : '꺼짐';
}

/**
 * 명령마다 이름·그 명령을 아는 첫 판·누르기 전에 되묻는 말. `note` 는 그 명령의 대가 한 줄 — 한 대짜리 확인(`confirm`)과
 * 여러 대짜리 확인이 같은 말을 쓴다.
 */
export const COMMANDS: Readonly<
  Record<AgentCommandKind, { label: string; since: string; note: string; confirm: (name: string) => string }>
> = {
  reboot: {
    label: '기기 재부팅',
    since: '0.7.6',
    note: '재부팅하는 동안 재생이 멈춥니다.',
    confirm: (name) => `"${name}" 기기를 재부팅합니다.\n\n${COMMANDS.reboot.note}`,
  },
  quit: {
    label: '앱 종료',
    since: '0.7.7',
    note: '콘솔에서 다시 켤 수 없어 현장에서 앱을 다시 실행해야 합니다.',
    confirm: (name) => `"${name}" 의 에이전트 앱을 종료합니다.\n\n${COMMANDS.quit.note}`,
  },
  power_off: {
    label: '전원 종료',
    since: '0.7.7',
    note: '콘솔에서 다시 켤 수 없어 현장에서 전원을 넣어야 합니다.',
    confirm: (name) => `"${name}" 기기의 전원을 끕니다.\n\n${COMMANDS.power_off.note}`,
  },
  exit_maintenance: {
    label: '정비 모드 해제',
    since: '0.7.17',
    note: '현장에서 기술자가 작업 중이면 그 화면이 닫힙니다.',
    confirm: (name) => `"${name}" 의 정비 화면을 닫고 재생으로 돌아갑니다.\n\n${COMMANDS.exit_maintenance.note}`,
  },
};

/** 되돌릴 수 없거나 재생을 끊는 명령 — 확인 창을 빨갛게. 정비 모드 해제는 재생을 되돌리는 쪽이다. */
export const commandIsDanger = (kind: AgentCommandKind): boolean => kind !== 'exit_maintenance';

/** 이 화면이 알아듣는 명령. 옛 서버·0.7.6 은 알리지 않는다 — 재부팅만 아는 것으로 본다. */
function knownCommands(control: AgentControl | undefined): AgentCommandKind[] {
  const commands = control?.reported?.commands;
  return commands && commands.length > 0 ? commands : ['reboot'];
}

/**
 * 이 명령을 걸 수 없는 까닭. 걸 수 있으면 null.
 *
 * 서버도 같은 것을 409 로 막는다 — 단추를 먼저 막아 눌러 보고 나서야 알게 하지 않는다.
 * 눌렀는데 아무 일도 없으면 현장에 사람을 보내야 하는지조차 모른다.
 */
export function commandBlock(
  display: Pick<Display, 'online' | 'agent_control'> & { status?: Pick<Display['status'], 'maintenance'> },
  kind: AgentCommandKind,
): string | null {
  const control = display.agent_control;
  const { label, since } = COMMANDS[kind];
  if (!display.online) return `화면이 오프라인이라 ${label}할 수 없습니다`;
  if (kind === 'exit_maintenance' && display.status?.maintenance !== true) return '정비 모드가 아닙니다';
  // 정비 중에는 다른 명령을 하지 않는다 — 화면도 거절한다(서버 `in_maintenance`).
  if (kind !== 'exit_maintenance' && display.status?.maintenance === true) {
    return `현장에서 정비 중입니다 — 정비 모드를 해제하거나 정비가 끝난 뒤에 ${label}하세요`;
  }
  if (!control?.reported || !knownCommands(control).includes(kind)) {
    return `원격 ${label}: 에이전트 ${since} 이상이 필요합니다`;
  }
  if (kind === 'reboot' && control.reported.reboot !== true) {
    return '재부팅 권한이 없습니다(루트 또는 기기 소유자 필요). 현장에서 전원을 껐다 켜세요';
  }
  if (kind === 'power_off' && control.reported.power_off !== true) {
    return '전원 종료 권한이 없습니다(루트 필요). 현장에서 끄세요';
  }
  if (control.pending) return `${COMMANDS[control.pending.kind].label} 명령을 보냈습니다. 화면이 받기를 기다립니다`;
  return null;
}

/** 재부팅만 막는지 볼 때 — 예전 이름. */
export const rebootBlock = (display: Pick<Display, 'online' | 'agent_control'>): string | null =>
  commandBlock(display, 'reboot');

/** 명령의 지금 상태 한 줄. 보낸 적이 없으면 null. */
export function commandStatus(
  control: AgentControl | undefined,
  now: Date = new Date(),
): { text: string; tone: 'muted' | 'ok' | 'bad' } | null {
  if (control?.pending) {
    return {
      text: `${COMMANDS[control.pending.kind].label} 보냄 · 수신 대기 중 (${relativeTime(control.pending.requested_at, now)})`,
      tone: 'muted',
    };
  }
  const last = control?.last_command;
  if (!last) return null;
  const label = COMMANDS[last.kind].label;
  if (last.state === 'started') return { text: `${label} — 시작했습니다 · ${relativeTime(last.at, now)}`, tone: 'ok' };
  return { text: `${label} — 하지 못했습니다${last.detail ? `: ${last.detail}` : ''} · ${relativeTime(last.at, now)}`, tone: 'bad' };
}

const TONE_CLASS = { muted: 'text-slate-500', ok: 'text-emerald-700', bad: 'text-rose-700' } as const;

const CHOICES: ReadonlyArray<{ value: Choice; label: string }> = [
  { value: 'device', label: '기기에 맡김' },
  { value: 'on', label: '켜기' },
  { value: 'off', label: '끄기' },
];

/**
 * 기기에 맡김 · 켜기 · 끄기 — 세 갈래를 한 번에 보이는 구간 선택으로. 펼침 목록에 넣으면
 * 지금 무엇이 골라져 있는지 한 번 더 눌러 봐야 안다.
 */
export function ChoiceSegment({
  value,
  label,
  disabled,
  onChange,
}: {
  value: boolean | null | undefined;
  /** 화면 낭독기가 읽는 묶음 이름. */
  label: string;
  disabled: boolean;
  onChange: (next: boolean | null) => void;
}): ReactNode {
  const current = choiceOf(value);
  return (
    <div className="seg" role="group" aria-label={label}>
      {CHOICES.map((choice) => (
        <button
          key={choice.value}
          type="button"
          className="seg-btn"
          aria-pressed={current === choice.value}
          disabled={disabled}
          onClick={() => {
            if (current !== choice.value) onChange(valueOf(choice.value));
          }}
        >
          {choice.label}
        </button>
      ))}
    </div>
  );
}

/** 원격 설정을 모르는 에이전트(0.7.6 미만) — 정해 두어도 업데이트하기 전에는 따르지 않는다. */
export function AgentOutdatedNotice({ display }: { display: Pick<Display, 'agent_control'> }): ReactNode {
  const control = display.agent_control;
  if (!control || control.reported) return null;
  return (
    <div className="settings-notice" role="status">
      원격 설정 미지원 에이전트(0.7.6 미만) · 업데이트한 뒤부터 적용됩니다
    </div>
  );
}

const COMMAND_ICON = { reboot: RotateCw, quit: LogOut, power_off: Power, exit_maintenance: Play } as const;

/**
 * 여러 화면에 건 명령의 결과 한 줄과, 걸지 않은 화면을 까닭별로 묶은 것.
 *
 * "3대에 보냈습니다" 만 말하면 나머지 둘이 왜 빠졌는지 화면마다 열어 봐야 안다. 까닭은 서버의 말(한 대짜리 409 와
 * 같은 문구)을 그대로 쓰고, 같은 까닭끼리 이름을 모은다.
 */
export function bulkCommandReport(result: BulkCommandResult): {
  summary: string;
  skipped: Array<{ message: string; names: string[] }>;
} {
  const label = COMMANDS[result.kind].label;
  const parts = [`${label} — ${result.issued}대에 보냈습니다`];
  if (result.already > 0) parts.push(`${result.already}대는 이미 보낸 상태`);
  if (result.skipped.length > 0) parts.push(`${result.skipped.length}대는 건너뜀`);
  const groups = new Map<string, { message: string; names: string[] }>();
  for (const row of result.skipped) {
    const group = groups.get(row.code) ?? { message: row.message, names: [] };
    group.names.push(row.name || '(이름 없음)');
    groups.set(row.code, group);
  }
  return { summary: parts.join(' · '), skipped: [...groups.values()] };
}

/** 재부팅 · 앱 종료 · 전원 종료 단추와 마지막 명령의 상태. */
export function RemoteCommands({
  display,
  pending,
  onCommand,
}: {
  display: Display;
  pending: boolean;
  onCommand: (kind: AgentCommandKind) => void;
}): ReactNode {
  const status = commandStatus(display.agent_control);
  const name = display.name || '(이름 없음)';
  const ask = async (kind: AgentCommandKind): Promise<void> => {
    const ok = await confirmDialog({
      title: `${COMMANDS[kind].label} — ${name}`,
      body: COMMANDS[kind].confirm(name),
      confirmLabel: COMMANDS[kind].label,
      danger: commandIsDanger(kind),
    });
    if (ok) onCommand(kind);
  };
  return (
    <>
      {/*
        재부팅만 밖에 둔다. 앱 종료·전원 종료는 원격으로 되돌릴 수 없어(현장에서 다시 켜야 한다) [⋯] 뒤에 빨갛게 —
        늘 보이는 빨간 단추 둘은 재부팅을 누르려던 손이 스치는 자리다(점진적 공개). 확인 창은 셋 다 그대로다.
      */}
      <div className="flex flex-wrap items-center gap-2">
        {/* 정비 중일 때만 — 현장에서 정비 화면을 열어 둔 채 떠나 재생이 멈춘 화면을 되돌린다. */}
        {displayLive(display).maintenance ? (
          <button
            type="button"
            className="btn btn-primary"
            disabled={pending || commandBlock(display, 'exit_maintenance') !== null}
            title={commandBlock(display, 'exit_maintenance') ?? undefined}
            onClick={() => void ask('exit_maintenance')}
          >
            <Play className="h-3.5 w-3.5" strokeWidth={2} />
            {COMMANDS.exit_maintenance.label}
          </button>
        ) : null}
        {(() => {
          const block = commandBlock(display, 'reboot');
          const Icon = COMMAND_ICON.reboot;
          return (
            <button
              type="button"
              className="btn"
              disabled={pending || block !== null}
              title={block ?? undefined}
              onClick={() => void ask('reboot')}
            >
              <Icon className="h-3.5 w-3.5" strokeWidth={2} />
              {COMMANDS.reboot.label}
            </button>
          );
        })()}
        <MoreMenu
          size="normal"
          label={`${name} 원격 명령 더 보기`}
          items={(['quit', 'power_off'] as const).map((kind) => {
            const block = commandBlock(display, kind);
            return {
              label: COMMANDS[kind].label,
              danger: true,
              disabled: pending || block !== null,
              title: block ?? undefined,
              onSelect: () => void ask(kind),
            };
          })}
        />
      </div>
      {status ? <span className={`text-[13px] ${TONE_CLASS[status.tone]}`}>{status.text}</span> : null}
    </>
  );
}
